import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'sonner';

import {
  type EnrollmentMode,
  type EnrollmentSource,
  isAlreadyEnrolledNoop,
  type PreviewChargeBreakdown,
  type PreviewEnrollmentParams,
} from '~entities/enrollment/api/preview-enrollment';

import { baseAPI } from '~shared/api/base-api';
import { KEYS } from '~shared/api/query-keys';

export type { EnrollmentMode, EnrollmentSource };

export interface StartEnrollmentParams extends PreviewEnrollmentParams {
  source?: EnrollmentSource;
  mode?: EnrollmentMode;
  /** When true, skip the success toast (bulk still invalidates queries). */
  suppressSuccessToast?: boolean;
}

export interface StartEnrollmentUnitResult {
  studentId: string;
  entityId: string;
  entityType: string;
  status: 'completed' | 'failed' | 'skipped';
  message?: string;
}

export interface StartEnrollmentResponse {
  status: number;
  message: string;
  outcome?: 'success' | 'partial' | 'failed';
  existingEnrollmentsList?: string[];
  validation?: unknown;
  charges?: PreviewChargeBreakdown[];
  results?: StartEnrollmentUnitResult[];
  transactions?: unknown[];
  subscriptions?: unknown[];
  amountDueTotal?: number;
}

export type StartEnrollmentOutcome =
  | { type: 'success'; message: string }
  | { type: 'partial'; message: string }
  | { type: 'already_enrolled'; message: string }
  | { type: 'error'; message: string };

export const getStartEnrollmentOutcome = (
  data: StartEnrollmentResponse,
): StartEnrollmentOutcome => {
  if (isAlreadyEnrolledNoop(data)) {
    return {
      type: 'already_enrolled',
      message:
        data.message ||
        `Student already enrolled: ${(data.existingEnrollmentsList ?? []).join(', ')}`,
    };
  }

  if (data.status !== 200 || data.outcome === 'failed') {
    return {
      type: 'error',
      message:
        data.message || 'No students were enrolled. Nothing was charged.',
    };
  }

  if (data.outcome === 'partial') {
    return {
      type: 'partial',
      message: data.message || 'Enrollment partially succeeded',
    };
  }

  // ENR-3580: never toast success without a completed unit or charge evidence.
  const hasCompletedUnit = (data.results ?? []).some(
    (result) => result.status === 'completed',
  );
  const hasCharges = (data.charges ?? []).length > 0;
  if (!hasCompletedUnit && !hasCharges) {
    return {
      type: 'error',
      message:
        data.message || 'No students were enrolled. Nothing was charged.',
    };
  }

  return {
    type: 'success',
    message: data.message || 'Students enrolled successfully!',
  };
};

export const startEnrollment = async (params: StartEnrollmentParams) => {
  const { suppressSuccessToast: _suppressSuccessToast, ...body } = params;
  const response = await baseAPI.post<StartEnrollmentResponse>(
    '/v2/enrollments',
    {
      studioId: body.studioId,
      requests: body.requests,
      source: body.source ?? 'studio_portal',
      mode: body.mode ?? 'manual',
      paymentProvider: body.paymentProvider,
      discountCategory: body.discountCategory ?? undefined,
      discountType: body.discountType,
      discountAmount: body.discountAmount,
      discountPercentage: body.discountPercentage,
      applyDiscountTo: body.applyDiscountTo,
    },
  );
  return response.data;
};

export const useStartEnrollment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: startEnrollment,
    onSuccess: (data, params) => {
      const outcome = getStartEnrollmentOutcome(data);
      if (outcome.type === 'already_enrolled') {
        toast.warning(outcome.message);
        return;
      }
      if (outcome.type === 'error') {
        toast.error(outcome.message);
        return;
      }
      if (outcome.type === 'partial') {
        toast.warning(outcome.message);
      } else if (!params.suppressSuccessToast) {
        toast.success(outcome.message);
      }

      const primaryEntity = params.requests[0];
      const sessionId = primaryEntity?.sessionId;

      void queryClient.invalidateQueries({ queryKey: [KEYS.STUDENTS] });
      void queryClient.invalidateQueries({ queryKey: [KEYS.CLASSES] });
      void queryClient.invalidateQueries({ queryKey: [KEYS.EVENTS] });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.STUDIO_DETAILS_ALL],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.ENROLLMENT_TO_STUDENT_RATIO, sessionId],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.TOTAL_ACTIVE_CLASSES, sessionId],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.ENROLLMENT_PERCENTAGE_OVER_LAST_MONTH, sessionId],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.COLLECTED_REVENUE_TOTAL, sessionId],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.TOTAL_OUTSTANDING_REVENUE, sessionId],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.ACCOUNTS_RECEIVABLE, sessionId],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.ACCOUNTS_RECEIVABLE_STUDENT_GROUPING, sessionId],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.TOTAL_SCHEDULED_UPCOMING_REVENUE, sessionId],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.COLLECTED_REVENUE_MONTHLY_TREND, sessionId],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.TOTAL_ENROLLMENTS_OVER_TIME],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.TOP_10_ENROLLED_STUDENTS, sessionId],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.REVENUE_TREND, sessionId],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.ENROLLMENT_GROWTH, sessionId],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.PAYMENT_HEALTH, sessionId],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.CLASS_PERFORMANCE_LOW_ENROLLMENT, sessionId],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.CLASS_PERFORMANCE_NEAR_CAPACITY, sessionId],
      });
      void queryClient.invalidateQueries({
        queryKey: [KEYS.NINETY_PERCENT_FULL_CLASSES, sessionId].filter(Boolean),
      });

      if (primaryEntity?.entityType === 'event') {
        void queryClient.invalidateQueries({
          queryKey: [KEYS.EVENTS, primaryEntity.entityId],
        });
        void queryClient.invalidateQueries({
          queryKey: [KEYS.STUDENTS, primaryEntity.entityId],
        });
      }
    },
    onError: (error: unknown) => {
      const message =
        axios.isAxiosError(error) &&
        typeof error.response?.data === 'object' &&
        error.response.data &&
        'message' in error.response.data &&
        typeof (error.response.data as { message: unknown }).message ===
          'string'
          ? (error.response.data as { message: string }).message
          : 'Enrollment failed.';
      toast.error(message);
    },
  });
};
